import { useEffect, useMemo, useState } from "react";
import { X, Loader2, ArrowRight, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Dialog for admin / GM / supervisor / depot_manager to correct a wrongly-captured
 * carrier on a specific gate event. Calls
 * ``POST /api/gate-events/{id}/correct-carrier`` which:
 *   1. updates ``gate_events.carrier_code`` on this event
 *   2. updates the ``containers`` cache row (when this is the latest event)
 *   3. retags open-period ``wash_orders.customer_code`` on the same tank
 *      (REQUESTED / IN_PROGRESS / COMPLETED — never CERTIFIED)
 *   4. updates ``edi_messages`` / ``edi_pending`` keyed to this event
 *   5. flags ``edi_resend_pending=true`` if the original CODECO already fired
 *   6. writes a ``gate_event.correct_carrier`` audit row
 *
 * We do a dry-run first (``apply: false``) so the operator sees the exact
 * downstream impact (counts per collection) before committing.
 */
export default function CorrectCarrierDialog({ event, onClose, onDone }) {
  const [carriers, setCarriers] = useState([]);
  const [targetCarrier, setTargetCarrier] = useState("");
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!event) return;
    setErr("");
    setPreview(null);
    setReason("");
    setTargetCarrier("");
    api.get("/partners?role=carrier&fields=code,legal_name")
      .then((r) => {
        const sorted = (r.data || [])
          .filter((p) => p.code)
          .sort((a, b) => (a.code || "").localeCompare(b.code || ""));
        setCarriers(sorted);
      })
      .catch(() => {
        // Fallback: the /containers/carriers endpoint returns just codes.
        api.get("/containers/carriers")
          .then((r) => setCarriers((r.data || []).map((c) => ({ code: c }))))
          .catch(() => setCarriers([]));
      });
  }, [event]);

  const currentCarrier = useMemo(
    () => event?.carrier_code || event?.last_carrier || "",
    [event],
  );

  if (!event) return null;

  const canSubmit = targetCarrier && targetCarrier.toUpperCase() !== currentCarrier.toUpperCase();

  const runPreview = async () => {
    setErr("");
    setPreviewLoading(true);
    try {
      const { data } = await api.post(`/gate-events/${event.id}/correct-carrier`, {
        new_carrier_code: targetCarrier.toUpperCase(),
        expected_current_carrier_code: currentCarrier || undefined,
        reason: reason || undefined,
        apply: false,
      });
      setPreview(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setPreviewLoading(false);
    }
  };

  const apply = async () => {
    setErr("");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/gate-events/${event.id}/correct-carrier`, {
        new_carrier_code: targetCarrier.toUpperCase(),
        expected_current_carrier_code: currentCarrier || undefined,
        reason: reason || undefined,
        apply: true,
      });
      const changed = data.updated?.gate_events ?? 0;
      if (changed > 0) {
        const new_cc = data.new_carrier_code || targetCarrier.toUpperCase();
        toast.success(`Carrier corrected: ${currentCarrier} → ${new_cc}`);
        onDone?.();
      } else {
        toast.warning("No changes were made (event may already be at target).");
      }
      onClose();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="correct-carrier-dialog">
      <div className="bg-white w-full max-w-lg rounded-lg shadow-2xl">
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-mono">Admin correction</div>
            <h2 className="text-lg font-bold text-gray-900 mt-0.5">Change carrier on gate event</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="close-correct-carrier">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Context: what event are we editing? */}
          <div className="grid grid-cols-3 gap-3 bg-gray-50 border border-gray-200 rounded-md p-3 text-xs">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-mono">Container</div>
              <div className="font-mono font-bold mt-0.5">{event.container_number}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-mono">Gate</div>
              <div className="font-bold mt-0.5">{event.gate_type}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-mono">Event time</div>
              <div className="font-mono mt-0.5">
                {event.event_time ? new Date(event.event_time).toLocaleString() : "—"}
              </div>
            </div>
          </div>

          {/* Before → After */}
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 font-mono mb-1">
                Currently
              </label>
              <div className="border border-gray-300 rounded-md px-3 py-2 bg-gray-50 font-mono font-bold text-gray-800">
                {currentCarrier || <span className="text-gray-400 font-normal">—</span>}
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400 shrink-0 mt-5" />
            <div className="flex-1">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 font-mono mb-1">
                Change to
              </label>
              <select
                value={targetCarrier}
                onChange={(e) => { setTargetCarrier(e.target.value); setPreview(null); }}
                className="ps-input w-full font-mono font-bold"
                data-testid="target-carrier-select"
              >
                <option value="">— select carrier —</option>
                {carriers.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code}{c.legal_name ? ` · ${c.legal_name}` : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 font-mono mb-1">
              Reason (for audit trail)
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="ps-input w-full"
              placeholder="e.g. Operator picked wrong carrier at gate-IN"
              maxLength={200}
              data-testid="correct-carrier-reason"
            />
          </div>

          {/* Preview panel */}
          {preview && (
            <div className="border border-blue-200 bg-blue-50 rounded-md p-3 text-xs" data-testid="correct-carrier-preview">
              <div className="flex items-center gap-2 text-blue-900 font-bold mb-2">
                <CheckCircle2 className="w-4 h-4" /> Ready to apply
              </div>
              <div className="text-blue-900/80 space-y-0.5">
                <div>
                  <span className="font-semibold">{preview.old_carrier_code}</span>
                  <ArrowRight className="w-3 h-3 inline mx-1" />
                  <span className="font-semibold">{preview.new_carrier_code}</span>
                  {preview.new_carrier_legal_name && (
                    <span className="text-blue-900/60"> ({preview.new_carrier_legal_name})</span>
                  )}
                </div>
                <div className="text-[11px] font-mono mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5">
                  <span>Gate event:</span><span className="text-right">{preview.matched?.gate_events || 0}</span>
                  <span>Containers cache:</span><span className="text-right">{preview.matched?.containers || 0}</span>
                  <span>Open wash orders:</span><span className="text-right">{preview.matched?.wash_orders_open || 0}</span>
                  <span>EDI messages:</span><span className="text-right">{preview.matched?.edi_messages || 0}</span>
                  <span>EDI pending:</span><span className="text-right">{preview.matched?.edi_pending || 0}</span>
                </div>
                {preview.edi_resend_would_flag && (
                  <div className="text-amber-800 mt-1 flex items-start gap-1.5 text-[11px]">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>Original CODECO already fired — will flag for re-send after apply.</span>
                  </div>
                )}
                {!preview.is_latest_event_on_container && (
                  <div className="text-amber-800 mt-1 flex items-start gap-1.5 text-[11px]">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>This isn't the latest event on the container — the containers cache (current carrier) will NOT be rewritten.</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {err && (
            <div className="border border-red-200 bg-red-50 text-red-800 rounded-md p-3 text-xs flex items-start gap-2" data-testid="correct-carrier-error">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{err}</span>
            </div>
          )}

          <div className="text-[11px] text-gray-500 border-l-2 border-gray-300 pl-3">
            Updates the gate event + container cache + open wash orders + EDI records keyed to this event.
            CERTIFIED washes already on an invoice are <b>not</b> touched — use the wash's "Correct customer" action for those.
            Full audit log entry recording who made the change and when.
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="ps-btn ps-btn-secondary"
            disabled={submitting}
            data-testid="cancel-correct-carrier"
          >
            Cancel
          </button>
          {!preview ? (
            <button
              onClick={runPreview}
              disabled={!canSubmit || previewLoading}
              className="ps-btn ps-btn-secondary"
              data-testid="preview-correct-carrier"
            >
              {previewLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking…</> : "Preview change"}
            </button>
          ) : (
            <button
              onClick={apply}
              disabled={submitting}
              className="ps-btn ps-btn-primary"
              data-testid="apply-correct-carrier"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Applying…</> : "Apply correction"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
